/**
 * PlatformContext — Universal State Layer
 * ────────────────────────────────────────
 * Provides system-wide state that persists across ALL page navigations:
 *
 *   1. useUnifiedSearch  → single search query + debounced results
 *   2. useUnifiedFilters → zone, sort, type, quick filters
 *   3. useUnifiedLineage → lineage computation for ANY project array
 *   4. Global project cache → map data available to every page
 *
 * Philosophy:
 *   - Every page READS from the same state
 *   - Every page RENDERS differently (map nodes, cards, list items)
 *   - Navigating between pages preserves context
 *   - Filter on Explore → switch to Map → same filters active
 *   - Click lineage on Profile → Journey tab opens with that chain
 *
 * Provider nesting order in App.jsx:
 *   AuthProvider > NSFWProvider > RealtimeProvider > Router >
 *     PlatformProvider > TrayProvider > Routes
 *
 * PlatformProvider sits OUTSIDE TrayProvider because TrayContext needs
 * access to platform search results, and PlatformContext needs nothing
 * from TrayContext. TrayContext already reads from AuthContext which is
 * further up the tree.
 *
 * Place at: src/context/PlatformContext.jsx
 */

import React, {
  createContext, useContext, useState, useCallback,
  useEffect, useMemo, useRef,
} from 'react';
import { mapAPI, projectsAPI, challengesAPI, api } from '../api';
import { ZONES, getProjectZone } from '../config/zones';

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────────────────────

const PlatformContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// DEBOUNCE UTILITY
// ─────────────────────────────────────────────────────────────────────────────

function useDebounceValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─────────────────────────────────────────────────────────────────────────────
// LINEAGE INDEX (pure logic, no React — reusable by hook and by standalone)
// ─────────────────────────────────────────────────────────────────────────────

function getParentId(project) {
  return project.parent_project_id || project.parentProjectId || null;
}

function getParentIds(project) {
  return project.parent_project_ids || project.parentProjectIds || [];
}

function getCreatedAt(project) {
  return project.created_at || project.createdAt || '';
}

function allParentIdsOf(project) {
  const single = getParentId(project);
  const multi = getParentIds(project);
  if (!single && (!multi || multi.length === 0)) return [];
  const set = new Set(multi);
  if (single) set.add(single);
  return Array.from(set);
}

/**
 * Build adjacency indexes from a flat project array.
 * Returns { projectById, parentIndex, childIndex }.
 */
function buildLineageIndex(projects) {
  const byId = new Map();
  const parents = new Map();
  const children = new Map();

  for (const p of projects) {
    byId.set(p.id, p);
    const pids = allParentIdsOf(p);
    parents.set(p.id, pids);
    for (const pid of pids) {
      if (!children.has(pid)) children.set(pid, []);
      children.get(pid).push(p.id);
    }
  }

  return { projectById: byId, parentIndex: parents, childIndex: children };
}

/**
 * Walk full ancestor + descendant tree from a project using BFS.
 * Returns Set<id> of all connected node IDs.
 */
function walkLineage(projectId, parentIndex, childIndex) {
  const visited = new Set([projectId]);

  // Walk UP
  const upQueue = [projectId];
  while (upQueue.length > 0) {
    const id = upQueue.pop();
    const pids = parentIndex.get(id);
    if (pids) {
      for (const pid of pids) {
        if (!visited.has(pid)) { visited.add(pid); upQueue.push(pid); }
      }
    }
  }

  // Walk DOWN
  const downQueue = [projectId];
  while (downQueue.length > 0) {
    const id = downQueue.pop();
    const cids = childIndex.get(id);
    if (cids) {
      for (const cid of cids) {
        if (!visited.has(cid)) { visited.add(cid); downQueue.push(cid); }
      }
    }
  }

  return visited;
}


// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

export function PlatformProvider({ children }) {

  // ══════════════════════════════════════════════════════════════════════════
  // 1. GLOBAL PROJECT CACHE
  // ══════════════════════════════════════════════════════════════════════════
  // One fetch of all map data, shared across every page.
  // Dashboard's useMapData becomes a consumer of this, not an independent fetch.

  const [allProjects, setAllProjects]     = useState([]);
  const [allChallenges, setAllChallenges] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [dataLoading, setDataLoading]     = useState(true);
  const [dataError, setDataError]         = useState(null);
  const mountedRef = useRef(true);

  const fetchAllData = useCallback(async () => {
    try {
      setDataLoading(true);
      setDataError(null);

      const [projectsRes, challengesRes] = await Promise.all([
        mapAPI.getProjects().catch(() => projectsAPI.getAll()),
        challengesAPI.getAll().catch(() => ({ data: { challenges: [] } })),
      ]);

      if (!mountedRef.current) return;

      const projects = projectsRes.data?.projects || projectsRes.data || [];
      const challenges = challengesRes.data?.challenges || challengesRes.data || [];

      // Build connections from parent relationships
      const connections = [];
      for (const project of projects) {
        const parentId = project.parent_project_id || project.parentProjectId;
        if (parentId) {
          connections.push({
            id: `project-${parentId}--project-${project.id}`,
            sourceId: parentId, targetId: project.id,
            sourceType: 'project', targetType: 'project',
          });
        }
        const parentIds = project.parent_project_ids || project.parentProjectIds || [];
        for (const pid of parentIds) {
          if (pid !== parentId) {
            connections.push({
              id: `project-${pid}--project-${project.id}`,
              sourceId: pid, targetId: project.id,
              sourceType: 'project', targetType: 'project',
            });
          }
        }
      }

      for (const challenge of challenges) {
        const projId = challenge.project_id || challenge.projectId;
        if (projId) {
          connections.push({
            id: `project-${projId}--challenge-${challenge.id}`,
            sourceId: projId, targetId: challenge.id,
            sourceType: 'project', targetType: 'challenge',
          });
        }
        const responseIds = challenge.response_project_ids || challenge.responseProjectIds || [];
        for (const rid of responseIds) {
          connections.push({
            id: `challenge-${challenge.id}--project-${rid}`,
            sourceId: challenge.id, targetId: rid,
            sourceType: 'challenge', targetType: 'project',
          });
        }
      }

      setAllProjects(projects);
      setAllChallenges(challenges);
      setAllConnections(connections);
    } catch (err) {
      if (mountedRef.current) setDataError(err.message || 'Failed to load data');
    } finally {
      if (mountedRef.current) setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchAllData();
    return () => { mountedRef.current = false; };
  }, [fetchAllData]);

  const mapData = useMemo(() => ({
    projects: allProjects,
    challenges: allChallenges,
    connections: allConnections,
  }), [allProjects, allChallenges, allConnections]);

  // Lineage index — built early because the surprising sort in applyFilters needs it.
  // The full lineage API (computeLineage, buildLineageChain, etc.) is defined in section 4.
  const lineageIndex = useMemo(() => {
    return buildLineageIndex(allProjects);
  }, [allProjects]);

  // ══════════════════════════════════════════════════════════════════════════
  // 2. UNIFIED SEARCH
  // ══════════════════════════════════════════════════════════════════════════
  // One search bar to rule them all. Pages read `searchQuery` and
  // `debouncedSearch` and filter their own view accordingly.
  // Also provides `searchResults` for instant dropdown suggestions.

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounceValue(searchQuery, 300);

  // Instant search results — projects, challenges, AND users
  const [searchResults, setSearchResults]           = useState([]);
  const [searchChallengeResults, setSearchChallengeResults] = useState([]);
  const [searchUserResults, setSearchUserResults]   = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchAbortRef = useRef(null);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.trim().length < 2) {
      setSearchResults([]);
      setSearchChallengeResults([]);
      setSearchUserResults([]);
      return;
    }

    // Cancel previous in-flight request
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setSearchLoading(true);

    (async () => {
      try {
        // Fire all three searches in parallel
        const [projectsRes, challengesRes, usersRes] = await Promise.allSettled([
          api.get('/projects/search', {
            params: { q: debouncedSearch, per_page: 8 },
            signal: controller.signal,
          }),
          api.get('/collaboration/challenges', {
            params: { q: debouncedSearch, limit: 4 },
            signal: controller.signal,
          }),
          api.get('/users/search', {
            params: { q: debouncedSearch, limit: 4 },
            signal: controller.signal,
          }),
        ]);

        if (!controller.signal.aborted) {
          setSearchResults(
            projectsRes.status === 'fulfilled'
              ? (projectsRes.value.data?.projects || projectsRes.value.data || [])
              : []
          );
          setSearchChallengeResults(
            challengesRes.status === 'fulfilled'
              ? (challengesRes.value.data?.challenges || challengesRes.value.data || []).slice(0, 4)
              : []
          );
          setSearchUserResults(
            usersRes.status === 'fulfilled'
              ? (usersRes.value.data?.users || usersRes.value.data || []).slice(0, 4)
              : []
          );
        }
      } catch (err) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.warn('Search failed:', err.message);
        }
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false);
      }
    })();

    return () => controller.abort();
  }, [debouncedSearch]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchChallengeResults([]);
    setSearchUserResults([]);
  }, []);

  const search = useMemo(() => ({
    query: searchQuery,
    debouncedQuery: debouncedSearch,
    setQuery: setSearchQuery,
    results: searchResults,
    challengeResults: searchChallengeResults,
    userResults: searchUserResults,
    loading: searchLoading,
    clear: clearSearch,
  }), [searchQuery, debouncedSearch, searchResults, searchChallengeResults, searchUserResults, searchLoading, clearSearch]);

  // ══════════════════════════════════════════════════════════════════════════
  // 3. UNIFIED FILTERS
  // ══════════════════════════════════════════════════════════════════════════
  // Exact persistence: same state on every page.

  const [activeZones, setActiveZones]           = useState([]);
  const [activeQuickFilters, setActiveQuickFilters] = useState([]);
  const [activeTypeFilters, setActiveTypeFilters]   = useState([]);
  const [activeShapeFilters, setActiveShapeFilters] = useState([]);
  const [sortBy, setSortBy]                         = useState('newest');
  const [showChallenges, setShowChallenges]         = useState(true);

  const toggleZone = useCallback((zoneId) => {
    setActiveZones(prev =>
      prev.includes(zoneId) ? prev.filter(z => z !== zoneId) : [...prev, zoneId]
    );
  }, []);

  const toggleQuickFilter = useCallback((filterId) => {
    setActiveQuickFilters(prev =>
      prev.includes(filterId) ? prev.filter(f => f !== filterId) : [...prev, filterId]
    );
  }, []);

  const toggleTypeFilter = useCallback((typeId) => {
    setActiveTypeFilters(prev =>
      prev.includes(typeId) ? prev.filter(t => t !== typeId) : [...prev, typeId]
    );
  }, []);

  const toggleShapeFilter = useCallback((shapeId) => {
    setActiveShapeFilters(prev =>
      prev.includes(shapeId) ? prev.filter(s => s !== shapeId) : [...prev, shapeId]
    );
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveZones([]);
    setActiveQuickFilters([]);
    setActiveTypeFilters([]);
    setActiveShapeFilters([]);
    setSortBy('newest');
    setShowChallenges(true);
  }, []);

  const activeFilterCount = useMemo(() => {
    return activeZones.length
      + activeQuickFilters.length
      + activeTypeFilters.length
      + activeShapeFilters.length
      + (sortBy !== 'newest' ? 1 : 0)
      + (!showChallenges ? 1 : 0);
  }, [activeZones, activeQuickFilters, activeTypeFilters, activeShapeFilters, sortBy, showChallenges]);

  /**
   * Apply all active filters to a project array.
   * This is the single source of truth for filtering logic.
   * Every page calls this with its own data set.
   *
   * @param {Object[]} projects - Array of projects to filter
   * @param {Object} options - Extra context for quick filters
   * @param {number|null} options.userId - Current user ID (for "mine" filter)
   * @param {number[]|Set} options.starredIds - Starred project IDs (for "starred" filter)
   * @returns {Object[]} Filtered and sorted projects
   */
  const applyFilters = useCallback((projects, { userId = null, starredIds = [] } = {}) => {
    let filtered = [...projects];
    const starredSet = starredIds instanceof Set ? starredIds : new Set(starredIds);

    // Text search (client-side fallback — pages with server search can skip this)
    if (debouncedSearch && debouncedSearch.trim().length >= 2) {
      const q = debouncedSearch.toLowerCase();
      filtered = filtered.filter(p => {
        const name = (p.name || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        const tags = (p.tags || []).map(t => (typeof t === 'string' ? t : t.name || '')).join(' ').toLowerCase();
        const author = (p.author?.username || p.author?.name || '').toLowerCase();
        return name.includes(q) || desc.includes(q) || tags.includes(q) || author.includes(q);
      });
    }

    // Zone filter
    if (activeZones.length > 0) {
      filtered = filtered.filter(p => {
        try {
          const zone = getProjectZone(p);
          return activeZones.includes(zone.id);
        } catch { return false; }
      });
    }

    // Type filter
    if (activeTypeFilters.length > 0) {
      filtered = filtered.filter(p => {
        const type = p.project_type || p.projectType || 'digital';
        return activeTypeFilters.includes(type);
      });
    }

    // Quick filters
    if (activeQuickFilters.includes('starred')) {
      filtered = filtered.filter(p => starredSet.has(p.id));
    }
    if (activeQuickFilters.includes('mine') && userId) {
      filtered = filtered.filter(p => {
        const ownerId = p.user_id || p.userId || p.author?.id;
        return ownerId === userId;
      });
    }
    if (activeQuickFilters.includes('challenges')) {
      filtered = filtered.filter(p =>
        (p.challenge_count || p.challengeCount || 0) > 0
      );
    }
    if (activeQuickFilters.includes('has_forks')) {
      filtered = filtered.filter(p =>
        (p.fork_count || p.forkCount || 0) > 0
      );
    }

    // Shape filter
    if (activeShapeFilters.length > 0) {
      filtered = filtered.filter(p => {
        let shapeTags = p.shape_tags || p.shapeTags || [];
        if (typeof shapeTags === 'string') {
          try { shapeTags = JSON.parse(shapeTags); } catch { shapeTags = []; }
        }
        if (!Array.isArray(shapeTags)) shapeTags = [];
        return shapeTags.some(s => activeShapeFilters.includes(s));
      });
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(getCreatedAt(b) || 0) - new Date(getCreatedAt(a) || 0));
        break;
      case 'most_forked':
        filtered.sort((a, b) => (b.fork_count || 0) - (a.fork_count || 0));
        break;
      case 'most_viewed':
        filtered.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
        break;
      case 'most_starred':
        filtered.sort((a, b) => (b.star_count || 0) - (a.star_count || 0));
        break;
      case 'surprising': {
        // Cross-zone forks are the magic — weight them heavily.
        // A project that was forked across disciplines is more surprising
        // than one forked within the same zone.
        const surpriseScore = (p) => {
          let score = 0;
          const forks = p.fork_count || p.forkCount || 0;
          const hasParent = p.parent_project_id || (p.parent_project_ids && p.parent_project_ids.length > 0);

          // Base: forks matter
          score += forks * 2;

          // Having lineage (being a fork) is interesting
          if (hasParent) score += 3;

          // Cross-zone detection: check if this project's zone differs from its parents
          const pids = allParentIdsOf(p);
          if (pids.length > 0) {
            let pZone = null;
            try { pZone = getProjectZone(p); } catch {}
            if (pZone) {
              for (const pid of pids) {
                const parent = lineageIndex.projectById.get(pid);
                if (parent) {
                  try {
                    const parentZone = getProjectZone(parent);
                    if (parentZone && parentZone.id !== pZone.id) {
                      // CROSS-ZONE FORK — this is the money
                      score += 15;
                      break; // one cross-zone parent is enough
                    }
                  } catch {}
                }
              }
            }
          }

          // Also check if any children are cross-zone
          const childIds = lineageIndex.childIndex.get(p.id) || [];
          if (childIds.length > 0) {
            let pZone = null;
            try { pZone = getProjectZone(p); } catch {}
            if (pZone) {
              for (const cid of childIds) {
                const child = lineageIndex.projectById.get(cid);
                if (child) {
                  try {
                    const childZone = getProjectZone(child);
                    if (childZone && childZone.id !== pZone.id) {
                      score += 10; // slightly less than being the cross-zone fork itself
                      break;
                    }
                  } catch {}
                }
              }
            }
          }

          // Multi-parent forks (convergence) are extra interesting
          if (pids.length > 1) score += 8;

          // Recency tiebreaker (within 90 days)
          const age = Date.now() - new Date(getCreatedAt(p) || 0).getTime();
          const recency = Math.max(0, 1 - age / (90 * 24 * 60 * 60 * 1000));
          score += recency * 3;

          return score;
        };
        filtered.sort((a, b) => surpriseScore(b) - surpriseScore(a));
        break;
      }
      default:
        break;
    }

    return filtered;
  }, [debouncedSearch, activeZones, activeTypeFilters, activeQuickFilters, activeShapeFilters, sortBy, lineageIndex]);

  const filters = useMemo(() => ({
    // State
    activeZones,
    activeQuickFilters,
    activeTypeFilters,
    activeShapeFilters,
    sortBy,
    showChallenges,
    activeFilterCount,

    // Togglers
    toggleZone,
    toggleQuickFilter,
    toggleTypeFilter,
    toggleShapeFilter,
    setSortBy,
    setShowChallenges,
    clearAllFilters,

    // The universal filter function
    applyFilters,
  }), [
    activeZones, activeQuickFilters, activeTypeFilters, activeShapeFilters,
    sortBy, showChallenges, activeFilterCount,
    toggleZone, toggleQuickFilter, toggleTypeFilter, toggleShapeFilter,
    clearAllFilters, applyFilters,
  ]);

  // ══════════════════════════════════════════════════════════════════════════
  // 4. UNIFIED LINEAGE
  // ══════════════════════════════════════════════════════════════════════════
  // Lineage computation that works against the global project cache.
  // lineageIndex is declared earlier (after mapData) so applyFilters can use it.

  /**
   * Get all IDs in a project's lineage tree (ancestors + descendants).
   * @param {Object|number} projectOrId - Project object or project ID
   * @returns {number[]} Array of all connected project IDs
   */
  const computeLineage = useCallback((projectOrId) => {
    const id = typeof projectOrId === 'object' ? projectOrId?.id : projectOrId;
    if (!id) return [];
    const visited = walkLineage(id, lineageIndex.parentIndex, lineageIndex.childIndex);
    return Array.from(visited);
  }, [lineageIndex]);

  /**
   * Build a sorted lineage chain (oldest → newest) of Project objects.
   * @param {Object|number} projectOrId - Project object or project ID
   * @returns {Object[]} Sorted array of project objects in the lineage
   */
  const buildLineageChain = useCallback((projectOrId) => {
    const ids = computeLineage(projectOrId);
    return ids
      .map(id => lineageIndex.projectById.get(id))
      .filter(Boolean)
      .sort((a, b) => {
        const aDate = getCreatedAt(a);
        const bDate = getCreatedAt(b);
        if (aDate && bDate) return new Date(aDate) - new Date(bDate);
        return a.id > b.id ? 1 : -1;
      });
  }, [computeLineage, lineageIndex]);

  /**
   * Check if two projects are in different zones (cross-zone connection).
   * @param {Object} projectA
   * @param {Object} projectB
   * @returns {{ isCrossZone: boolean, fromZone: Object|null, toZone: Object|null }}
   */
  const checkCrossZone = useCallback((projectA, projectB) => {
    if (!projectA || !projectB) return { isCrossZone: false, fromZone: null, toZone: null };
    try {
      const zoneA = getProjectZone(projectA);
      const zoneB = getProjectZone(projectB);
      return {
        isCrossZone: zoneA.id !== zoneB.id,
        fromZone: zoneA,
        toZone: zoneB,
      };
    } catch {
      return { isCrossZone: false, fromZone: null, toZone: null };
    }
  }, []);

  /**
   * Get a project object by ID from the global cache.
   * Useful for lineage chain hydration on any page.
   */
  const getProjectById = useCallback((id) => {
    return lineageIndex.projectById.get(id) || null;
  }, [lineageIndex]);

  const lineage = useMemo(() => ({
    computeLineage,
    buildLineageChain,
    checkCrossZone,
    getProjectById,
    // Expose the raw index for advanced consumers (like CosmicMap)
    parentIndex: lineageIndex.parentIndex,
    childIndex: lineageIndex.childIndex,
  }), [computeLineage, buildLineageChain, checkCrossZone, getProjectById, lineageIndex]);

  // ══════════════════════════════════════════════════════════════════════════
  // CONTEXT VALUE
  // ══════════════════════════════════════════════════════════════════════════

  const value = useMemo(() => ({
    // Global data
    mapData,
    dataLoading,
    dataError,
    refetchData: fetchAllData,
    allProjects,
    allChallenges,
    allConnections,

    // Unified systems
    search,
    filters,
    lineage,
  }), [
    mapData, dataLoading, dataError, fetchAllData,
    allProjects, allChallenges, allConnections,
    search, filters, lineage,
  ]);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSUMER HOOKS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Full platform context — use when you need everything.
 */
export function usePlatform() {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used within a PlatformProvider');
  return ctx;
}

/**
 * Just the search system — lighter import for components that only need search.
 */
export function useUnifiedSearch() {
  const { search } = usePlatform();
  return search;
}

/**
 * Just the filter system.
 */
export function useUnifiedFilters() {
  const { filters } = usePlatform();
  return filters;
}

/**
 * Just the lineage system.
 */
export function useUnifiedLineage() {
  const { lineage } = usePlatform();
  return lineage;
}

/**
 * Just the global project data.
 */
export function useGlobalData() {
  const { mapData, dataLoading, dataError, refetchData, allProjects, allChallenges, allConnections } = usePlatform();
  return { mapData, dataLoading, dataError, refetchData, allProjects, allChallenges, allConnections };
}

export default PlatformContext;